import {
  Col,
  Container,
  Form,
  Modal,
  Row,
  Table,
  Button as BootstrapButton,
} from "react-bootstrap";
import GetData from "../../services/getData";
import { useDebouncedSearch } from "../../hooks/useDebouncedSearch";
import kbIcon from "../../media/template_plan.png";
import BlockTitle from "../../components/layout/blockTitle/BlockTitle";
import TitleWithIcon from "../../components/layout/pageTitle/PageTitle";
import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { asPlaceholder } from "../../i18n/filterLabels";
import { getLocale } from "../../i18n/config";
import filterIcon from "../../media/filter_icon.png";

function KnowledgeBase() {
  const { t, i18n } = useTranslation("knowledge_base");
  const [articles, setArticles] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const {
    searchTerm,
    setSearchTerm,
    debouncedSearchTerm,
    searchInputRef,
    captureSearchFocus,
    restoreSearchFocus,
  } = useDebouncedSearch();
  const [filters, setFilters] = useState({ category: "all" });
  const [isFiltersActive, setIsFiltersActive] = useState(false);

  const [showModal, setShowModal] = useState(false);
  const [newArticle, setNewArticle] = useState({
    name: "",
    text: "",
    category_id: "",
  });

  const loadArticles = useCallback(async () => {
    const preserveSearchFocus = captureSearchFocus();
    setLoading(true);
    setError(null);
    try {
      const data = await GetData({
        module: "knowledge_base",
        block: "list",
        category: filters.category === "all" ? "" : String(filters.category),
        search: debouncedSearchTerm.trim(),
      });
      setArticles(data.result || []);
      const raw = data.categories_list ?? data.categories ?? [];
      setCategories(Array.isArray(raw) ? raw : []);
    } catch (err) {
      console.error("Ошибка загрузки статей:", err);
      setError(err.message || t("list.errors.loadFailed"));
    } finally {
      setLoading(false);
      restoreSearchFocus(preserveSearchFocus);
    }
  }, [filters.category, debouncedSearchTerm, captureSearchFocus, restoreSearchFocus]);

  useEffect(() => {
    loadArticles();
  }, [loadArticles]);

  useEffect(() => {
    document.title = t("list.title");
  }, [t]);

  const handleSaveArticle = async () => {
    try {
      await GetData({
        module: "knowledge_base",
        block: "list",
        action: "add",
        name: newArticle.name,
        text: newArticle.text,
        category_id: newArticle.category_id,
      });
      await loadArticles();
      setShowModal(false);
      setNewArticle({ name: "", text: "", category_id: "" });
    } catch (err) {
      console.error("Ошибка добавления:", err);
      alert(t("list.errors.addFailed"));
    }
  };

  const handleAddClick = () => {
    setNewArticle({ name: "", text: "", category_id: "" });
    setShowModal(true);
  };

  const handleModalClose = () => {
    setShowModal(false);
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setNewArticle((prev) => ({ ...prev, [name]: value }));
  };

  const clearFilters = () => {
    setSearchTerm("");
    setFilters({ category: "all" });
  };

  const handleSearchChange = (e) => {
    setSearchTerm(e.target.value);
  };

  const handleFilterChange = (filterType, value) => {
    setFilters((prev) => ({ ...prev, [filterType]: value }));
  };

  const formatDate = (v) => {
    if (!v) return "—";
    const d = new Date(v);
    if (Number.isNaN(d.getTime())) return String(v);
    return d.toLocaleString(getLocale(i18n.language), {
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  return (
    <Container fluid="md">
      <Row>
        <Col>
          <BlockTitle
            title={
              <TitleWithIcon
                icon={kbIcon}
                title={t("list.title")}
                showStatus={false}
                status={""}
              />
            }
            buttons={[
              {
                label: t("list.add"),
                onClick: handleAddClick,
                type: "normal",
              },
            ]}
          />
        </Col>
      </Row>
      <Row>
        <Col>
          <div className="page">
            <div className="page-content">
              <div className="page-block">
                <div
                  className={`filters-panel ${isFiltersActive ? "active" : ""}`}
                >
                  <div className="filters-panel-title">
                    <div className="filters-panel-title-icon">
                      <img
                        className="title-with-icon__image"
                        src={filterIcon}
                        alt={t("filters.alt", { ns: "common" })}
                        title={t("filters.alt", { ns: "common" })}
                      />
                      <div className="filters-panel-name">
                        {t("filters.title", { ns: "common" })}
                      </div>
                    </div>
                    <button
                      type="button"
                      className="button-default light"
                      onClick={() => setIsFiltersActive((prev) => !prev)}
                    >
                      {isFiltersActive
                        ? t("filters.hide", { ns: "common" })
                        : t("filters.show", { ns: "common" })}
                    </button>
                  </div>
                  <div className="search-section">
                    <input
                      ref={searchInputRef}
                      type="text"
                      placeholder={t("list.searchPlaceholder")}
                      value={searchTerm}
                      onChange={handleSearchChange}
                      className="search-input"
                    />
                  </div>
                  <div className="filters-section">
                    <div className="filter-group">
                      <select
                        className={
                          filters.category === "all" ? "is-placeholder" : ""
                        }
                        value={filters.category}
                        onChange={(e) =>
                          handleFilterChange("category", e.target.value)
                        }
                      >
                        <option value="all">
                          {asPlaceholder(t("list.categoryLabel"))}
                        </option>
                        {categories.map((c) => (
                          <option key={c.id} value={String(c.id)}>
                            {c.name}
                          </option>
                        ))}
                      </select>
                    </div>
                    <button
                      className="button-default light"
                      onClick={clearFilters}
                    >
                      {t("filters.reset", { ns: "common" })}
                    </button>
                  </div>
                </div>
              </div>
              <div className="page-block table-list">
                {error ? (
                  <div className="text-danger">
                    {t("error", { ns: "common", message: error })}
                  </div>
                ) : loading ? (
                  <div>{t("state.loading", { ns: "common" })}</div>
                ) : articles.length === 0 ? (
                  <div className="no-patients">{t("list.notFound")}</div>
                ) : (
                  <Table className="main-table-list rounded-table">
                    <thead>
                      <tr>
                        <th>{t("list.colName")}</th>
                        <th>{t("list.colCategory")}</th>
                        <th>{t("list.colChanged")}</th>
                      </tr>
                    </thead>
                    <tbody>
                      {articles.map((article) => {
                        const aid = article?.id?.value ?? article?.id;
                        return (
                          <tr key={aid}>
                            <td>
                              <Link
                                to={`/knowledge_base/${aid}/`}
                                className="table-item-link"
                              >
                                <div className="table-item-name">
                                  {article?.name}
                                </div>
                              </Link>
                            </td>
                            <td>{article?.category_name || "—"}</td>
                            <td>{formatDate(article?.date_change)}</td>
                          </tr>
                        );
                      })}
                      <tr className="table-summary">
                        <td colSpan={3} className="summary-cell">
                          {t("list.found", { count: articles.length })}
                        </td>
                      </tr>
                    </tbody>
                  </Table>
                )}
              </div>
            </div>
          </div>
        </Col>
      </Row>

      <Modal show={showModal} onHide={handleModalClose}>
        <Modal.Header closeButton>
          <Modal.Title>{t("list.addTitle")}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form>
            <Form.Group className="mb-3">
              <Form.Label>{t("modal.nameLabel")}</Form.Label>
              <Form.Control
                type="text"
                name="name"
                value={newArticle.name}
                onChange={handleInputChange}
                placeholder={t("modal.namePlaceholder")}
                required
              />
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>{t("list.colCategory")}</Form.Label>
              <Form.Select
                name="category_id"
                value={newArticle.category_id}
                onChange={handleInputChange}
              >
                <option value="">{t("modal.selectCategory")}</option>
                {categories.map((c) => (
                  <option key={c.id} value={String(c.id)}>
                    {c.name}
                  </option>
                ))}
              </Form.Select>
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>{t("modal.textLabel")}</Form.Label>
              <Form.Control
                as="textarea"
                name="text"
                rows={6}
                value={newArticle.text}
                onChange={handleInputChange}
                placeholder={t("modal.textPlaceholder")}
              />
            </Form.Group>
          </Form>
        </Modal.Body>
        <Modal.Footer>
          <BootstrapButton variant="secondary" onClick={handleModalClose}>
            {t("actions.cancel", { ns: "common" })}
          </BootstrapButton>
          <BootstrapButton
            variant="primary"
            onClick={handleSaveArticle}
            disabled={!newArticle.name.trim()}
          >
            {t("actions.save", { ns: "common" })}
          </BootstrapButton>
        </Modal.Footer>
      </Modal>
    </Container>
  );
}

export default KnowledgeBase;
